import React from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { FixedDaysPool } from '@/types/fixedDays.types';

interface FixedDaysHeaderProps {
  pools: FixedDaysPool[];
  selectedPool: FixedDaysPool | null;
  onPoolChange: (pool: FixedDaysPool) => void;
  histories: Array<{ uniqueFileID: number; linkName: string }>;
  selectedHistory: number | null;
  onHistoryChange: (fileId: number) => void;
  participants: string[];
  selectedParticipant: string | null;
  onParticipantChange: (participant: string | null) => void;
  onDownload: () => void;
  isLoading?: boolean;
  isLoadingHistories?: boolean;
}

export const FixedDaysHeader: React.FC<FixedDaysHeaderProps> = ({
  pools,
  selectedPool,
  onPoolChange,
  histories,
  selectedHistory,
  onHistoryChange,
  participants,
  selectedParticipant,
  onParticipantChange,
  onDownload,
  isLoading = false,
  isLoadingHistories = false,
}) => {
  const poolOptions = pools.map((pool) => ({
    label: pool.pool,
    value: pool.poolId.toString(),
  }));

  const historyOptions = histories.map((history) => ({
    label: history.linkName,
    value: history.uniqueFileID.toString(),
  }));

  const participantOptions = [
    { label: '-- None --', value: '' },
    ...participants.map((participant) => ({
      label: participant,
      value: participant,
    })),
  ];

  const handlePoolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const pool = pools.find((p) => p.poolId.toString() === event.target.value);
    if (pool) {
      onPoolChange(pool);
    }
  };

  const handleHistoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onHistoryChange(parseInt(event.target.value, 10));
  };

  const handleParticipantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onParticipantChange(event.target.value || null);
  };

  return (
    <div className="mb-6 rounded-xl border border-gray-200/70 bg-white/80 p-3 shadow-sm backdrop-blur dark:border-dark-700 dark:bg-dark-800/80">
      <div className="flex flex-wrap items-center gap-3">
        {/* Pool Selector */}
        <div className="min-w-[170px] flex-1">
          <select
            id="pool-select"
            aria-label="Pool"
            value={selectedPool?.poolId.toString() || ''}
            onChange={handlePoolChange}
            disabled={isLoading || pools.length === 0}
            className="block h-10 w-full cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white bg-right bg-no-repeat px-3 text-sm font-medium text-gray-900 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:disabled:bg-dark-900"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '1.25em 1.25em',
            }}
          >
            {poolOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* History Selector */}
        <div className="min-w-[220px] flex-[1.4]">
          <select
            id="history-select"
            aria-label="Period"
            value={selectedHistory?.toString() || ''}
            onChange={handleHistoryChange}
            disabled={isLoadingHistories || histories.length === 0}
            className="block h-10 w-full cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white bg-right bg-no-repeat px-3 text-sm font-medium text-gray-900 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:disabled:bg-dark-900"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '1.25em 1.25em',
            }}
          >
            {historyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Participant Selector */}
        <div className="min-w-[200px] flex-1">
          <select
            id="participant-select"
            aria-label="Participant"
            value={selectedParticipant || ''}
            onChange={handleParticipantChange}
            disabled={isLoading || participants.length === 0}
            className="block h-10 w-full cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white bg-right bg-no-repeat px-3 text-sm font-medium text-gray-900 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:disabled:bg-dark-900"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '1.25em 1.25em',
            }}
          >
            {participantOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Download Button */}
        <div className="min-w-[160px] flex-none">
          <Button
            onClick={onDownload}
            variant="secondary"
            disabled={isLoading || !selectedHistory}
            className="h-10 w-full"
            leftIcon={<Download />}
          >
            Download Excel
          </Button>
        </div>
      </div>
    </div>
  );
};
